import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { vaultApi, guardrailApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VaultCylinder from "@/components/shared/VaultCylinder";
import { MoneyDisplay } from "@/components/shared/MoneyDisplay";
import { ShieldAlert, ShieldCheck, ShieldX, AlertTriangle, ArrowRight, Zap, Textarea } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const VAULT_INFO = {
  TAX_CUSTODY: { desc: "VAT/PAYE funds belonging to the government. Never spendable for operations.", severity: "CRITICAL" },
  SUPPLIER_OBLIGATION: { desc: "Trade credit payables reserved for supplier payments only.", severity: "HIGH" },
  CUSTOMER_TRUST: { desc: "Customer deposits held in trust until delivery is confirmed.", severity: "CRITICAL" },
  INVENTORY_CUSTODY: { desc: "Value of goods received but not yet paid for.", severity: "HIGH" },
  OPERATING_CUSTODY: { desc: "Loan/overdraft proceeds. Usable but not owned.", severity: "MEDIUM" },
  FREE_EQUITY: { desc: "True owned surplus. Freely spendable within buffer limits.", severity: "LOW" },
};

export default function VaultsPage() {
  const { formatMoney } = useAuth();
  const [vaults, setVaults] = useState([]);
  const [selectedVault, setSelectedVault] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Guardrail simulator
  const [simOpen, setSimOpen] = useState(false);
  const [simVault, setSimVault] = useState("");
  const [simAmount, setSimAmount] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Override
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideResult, setOverrideResult] = useState(null);

  useEffect(() => {
    loadVaults();
  }, []);

  const loadVaults = async () => {
    try {
      const res = await vaultApi.list();
      setVaults(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadHistory = async (vaultId) => {
    try {
      const res = await vaultApi.history(vaultId);
      setHistory(res.data);
    } catch (e) { console.error(e); }
  };

  const selectVault = (vault) => {
    setSelectedVault(vault);
    loadHistory(vault.id);
  };

  const runSimulation = async () => {
    if (!simVault || !simAmount) return;
    setSimLoading(true);
    setSimResult(null);
    try {
      const res = await guardrailApi.previewSpend({
        from_vault_id: simVault,
        amount: parseFloat(simAmount),
        attempted_action: "SPEND",
      });
      setSimResult(res.data);
    } catch (e) {
      setSimResult({ decision: "ERROR", reasons: [e.response?.data?.detail || "Simulation failed"], simulation: {}, suggested_actions: [] });
    }
    setSimLoading(false);
  };

  const submitOverride = async () => {
    if (!overrideReason) return;
    try {
      const res = await guardrailApi.createOverride({
        vault_id: simVault,
        amount: parseFloat(simAmount),
        reason_text: overrideReason,
      });
      setOverrideResult(res.data);
      loadVaults();
    } catch (e) { console.error(e); }
  };

  const vaultOrder = ["TAX_CUSTODY", "SUPPLIER_OBLIGATION", "CUSTOMER_TRUST", "INVENTORY_CUSTODY", "OPERATING_CUSTODY", "FREE_EQUITY"];
  const sortedVaults = vaultOrder.map(t => vaults.find(v => v.vault_type === t)).filter(Boolean);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="vaults-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Vault Architecture</h1>
          <p className="text-sm text-slate-500 mt-0.5">Six-vault ownership enforcement system</p>
        </div>
        <Button onClick={() => setSimOpen(true)} className="bg-cyan-600 hover:bg-cyan-500 text-slate-50 gap-2" data-testid="open-guardrail-sim-btn">
          <Zap className="w-4 h-4" /> Guardrail Simulator
        </Button>
      </div>

      {/* Vault Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {sortedVaults.map((vault, idx) => (
          <div
            key={`${vault.vault_type || vault.id || idx}-${vault.currency || "native"}`}
            className={`p-3 rounded-xl border transition-all cursor-pointer ${selectedVault?.id === vault.id ? 'border-amber-500/50 bg-slate-800/40' : 'border-slate-800/30 bg-slate-900/30 hover:border-slate-700/50'}`}
          >
            <VaultCylinder vault={vault} formatMoney={formatMoney} onClick={selectVault} />
          </div>
        ))}
      </div>

      {/* Selected Vault Detail */}
      {selectedVault && (
        <Card className="bg-slate-900/40 border-slate-800/50" data-testid="vault-detail-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-barlow text-lg text-slate-50">{selectedVault.label}</CardTitle>
                <p className="text-xs text-slate-500 mt-1">{VAULT_INFO[selectedVault.vault_type]?.desc}</p>
              </div>
              <Badge className={`text-[10px] font-mono ${
                selectedVault.rules?.permission === "PROHIBITED" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                selectedVault.rules?.permission === "RESTRICTED" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" :
                "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
              } border`}>
                {selectedVault.rules?.permission}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Current Balance</span>
                  <MoneyDisplay amount={selectedVault.balance} size="xl" className="block mt-1 text-slate-50" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Vault Type</span>
                    <span className="text-slate-300 font-mono">{selectedVault.vault_type}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Permission</span>
                    <span className={`font-mono ${selectedVault.rules?.permission === "FREE" ? "text-emerald-400" : selectedVault.rules?.permission === "PROHIBITED" ? "text-red-400" : "text-amber-400"}`}>
                      {selectedVault.rules?.permission}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Currency</span>
                    <span className="text-slate-300 font-mono">{selectedVault.currency}</span>
                  </div>
                </div>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">30-Day Balance History</span>
                {history.length > 0 && (
                  <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={history}>
                      <defs>
                        <linearGradient id="vaultGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={selectedVault.color || "#06B6D4"} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={selectedVault.color || "#06B6D4"} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" hide />
                      <YAxis hide domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ background: 'rgba(10,16,32,0.97)', border: '1px solid rgba(120,180,255,0.25)', borderRadius: '12px', padding: '10px 14px', color: '#fff' }}
                        labelStyle={{ color: '#94A3B8' }}
                        formatter={(v) => [formatMoney(v), "Balance"]}
                      />
                      <Area type="monotone" dataKey="balance" stroke={selectedVault.color || "#06B6D4"} fill="url(#vaultGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Guardrail Simulator Dialog */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg" data-testid="guardrail-sim-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50">Guardrail Simulator</DialogTitle>
            <DialogDescription className="text-slate-500">Preview what happens when you attempt a spend from any vault.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">From Vault</Label>
              <Select value={simVault} onValueChange={setSimVault}>
                <SelectTrigger className="bg-slate-900/50 border-slate-700 mt-1" data-testid="sim-vault-select">
                  <SelectValue placeholder="Select vault" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {sortedVaults.map(v => (
                    <SelectItem key={v.id} value={v.id} className="text-sm">{v.label} ({formatMoney(v.balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Amount (AED)</Label>
              <Input
                type="number"
                value={simAmount}
                onChange={(e) => setSimAmount(e.target.value)}
                placeholder="5000"
                className="bg-slate-900/50 border-slate-700 mt-1"
                data-testid="sim-amount-input"
              />
            </div>
            <Button onClick={runSimulation} disabled={simLoading || !simVault || !simAmount} className="w-full bg-cyan-600 hover:bg-cyan-500" data-testid="sim-run-btn">
              {simLoading ? "Simulating..." : "Preview Spend"}
            </Button>

            {simResult && (
              <div className={`p-4 rounded-lg border ${
                simResult.decision === "ALLOW" ? "border-emerald-500/30 bg-emerald-500/5" :
                simResult.decision === "DENY" ? "border-red-500/30 bg-red-500/5" :
                "border-amber-500/30 bg-amber-500/5"
              }`} data-testid="sim-result">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {simResult.decision === "ALLOW" ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> :
                     simResult.decision === "DENY" ? <ShieldX className="w-5 h-5 text-red-400" /> :
                     <ShieldAlert className="w-5 h-5 text-amber-400" />}
                    <span className={`font-barlow font-bold text-lg ${
                      simResult.decision === "ALLOW" ? "text-emerald-400" : simResult.decision === "DENY" ? "text-red-400" : "text-amber-400"
                    }`}>
                      {simResult.decision}
                    </span>
                  </div>
                  {simResult.guardrail_level && (
                    <span className={`text-[9px] font-mono px-2 py-0.5 rounded-full border ${
                      simResult.guardrail_level === "BLOCK" ? "border-red-500/30 text-red-400 bg-red-500/10" :
                      simResult.guardrail_level === "REQUIRE_APPROVAL" ? "border-amber-500/30 text-amber-400 bg-amber-500/10" :
                      simResult.guardrail_level === "CONFIRM" ? "border-blue-500/30 text-blue-400 bg-blue-500/10" :
                      simResult.guardrail_level === "ADVISORY" ? "border-cyan-500/30 text-cyan-400 bg-cyan-500/10" :
                      "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                    }`} data-testid="guardrail-level-badge">
                      {simResult.guardrail_level}
                    </span>
                  )}
                </div>

                {/* AI Explanation — "Why?" panel */}
                {simResult.ai_explanation && (
                  <div className="p-2.5 rounded-lg bg-slate-800/40 border border-slate-700/20 mb-3" data-testid="ai-why-panel">
                    <span className="text-[9px] text-cyan-400 font-mono uppercase tracking-wider block mb-1">AI Explanation</span>
                    <p className="text-xs text-slate-300">{simResult.ai_explanation.explanation}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{simResult.ai_explanation.what_this_means}</p>
                    <p className="text-[11px] text-cyan-400/80 mt-1">{simResult.ai_explanation.what_to_do}</p>
                  </div>
                )}

                <ul className="space-y-1 mb-3">
                  {simResult.reasons?.map((r, i) => (
                    <li key={i} className="text-xs text-slate-400 flex items-start gap-1.5">
                      <span className="text-slate-600 mt-0.5">-</span>{r}
                    </li>
                  ))}
                </ul>

                {/* "What happens if I continue?" simulation */}
                {simResult.ai_simulation && (
                  <div className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/20 mb-3" data-testid="simulation-panel">
                    <span className="text-[9px] text-slate-500 uppercase tracking-wider block mb-2">Impact Simulation</span>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-center">
                        <div className="text-[10px] text-slate-600">Coverage After</div>
                        <div className={`text-sm font-mono ${simResult.ai_simulation.vault_coverage_after < 100 ? 'text-red-400' : 'text-emerald-400'}`}>
                          {simResult.ai_simulation.vault_coverage_after}%
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-slate-600">Risk Level</div>
                        <div className={`text-sm font-mono ${
                          simResult.ai_simulation.risk_level === 'CRITICAL' ? 'text-red-400' :
                          simResult.ai_simulation.risk_level === 'HIGH' ? 'text-amber-400' : 'text-emerald-400'
                        }`}>
                          {simResult.ai_simulation.risk_level}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-slate-600">Runway</div>
                        <div className="text-sm font-mono text-slate-300">{simResult.ai_simulation.projected_runway_days}d</div>
                      </div>
                      {simResult.ai_simulation.crunch_date && (
                        <div className="text-center">
                          <div className="text-[10px] text-slate-600">Crunch Date</div>
                          <div className="text-sm font-mono text-red-400">{simResult.ai_simulation.crunch_date}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {simResult.simulation?.vat_shortfall_days && (
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20 mb-3">
                    <span className="text-xs text-red-400 font-mono">VAT shortfall projected in {simResult.simulation.vat_shortfall_days} days</span>
                  </div>
                )}
                {/* Safe alternatives */}
                {(simResult.suggested_actions?.length > 0 || simResult.ai_simulation?.recommended_alternatives?.length > 0) && (
                  <div className="space-y-1 mb-3">
                    <span className="text-[10px] text-slate-500 uppercase">Safe Alternatives:</span>
                    {(simResult.ai_simulation?.recommended_alternatives || simResult.suggested_actions || []).map((a, i) => (
                      <p key={i} className="text-xs text-cyan-400">{a}</p>
                    ))}
                  </div>
                )}
                {simResult.decision !== "ALLOW" && (
                  <Button
                    variant="outline"
                    className="mt-1 w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    onClick={() => { setOverrideOpen(true); setSimOpen(false); }}
                    data-testid="request-override-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" /> Request Override
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Override Request
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This action will be logged and may affect your behavioural score. Overrides shift your state toward Survival mode.
            </DialogDescription>
          </DialogHeader>
          {!overrideResult ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-400">Reason for Override</Label>
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Explain why this override is necessary..."
                  className="w-full mt-1 p-3 rounded-lg bg-slate-900/50 border border-slate-700 text-sm text-slate-200 min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                  data-testid="override-reason-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOverrideOpen(false)} className="border-slate-700">Cancel</Button>
                <Button onClick={submitOverride} disabled={!overrideReason} className="bg-amber-600 hover:bg-amber-500 text-slate-900" data-testid="override-submit-btn">
                  Submit Override
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="override-result">
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-amber-400 font-medium">Override Recorded</p>
                <p className="text-xs text-slate-400 mt-1">Status: {overrideResult.status}</p>
              </div>
              {overrideResult.coaching && (
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Coaching Response:</p>
                  <p className="text-xs text-slate-400">{overrideResult.coaching}</p>
                </div>
              )}
              <Button onClick={() => { setOverrideOpen(false); setSimResult(null); setOverrideResult(null); setOverrideReason(""); }} className="w-full bg-slate-800 hover:bg-slate-700">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
